import React from 'react';
import { Table, Form } from 'react-bootstrap';
import { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { postRequestOptions, getRequestOptions } from "../helpers/Fetchwrapper";
import { Card } from 'react-bootstrap-v5';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { numberFormat, capitalize } from '../helpers/GlobalHelper';
import { Link, useLocation } from 'react-router-dom';
import { ReportSelectOption } from './LedgerStatementReport';

interface DayBookRow {
    idAccVouchers: number;
    voucherNo: string;
    voucherType: string;
    voucherDate: string;
    ledgerName: string;
    ledgerPath: string;
    ledgerAmount: number;
    ledgerDrCr: 'dr' | 'cr';
    narration: string;
}

interface DayBookReportProps {}

const defaultProjectOption: ReportSelectOption = { label: 'Select Project', value: '' };
const PaymentSummaryReport: React.FC<DayBookReportProps> = () => {
    const location = useLocation();
    const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<DayBookRow[]>([]);

    const [projects, setProjects] = useState<ReportSelectOption[]>([]);
    const [project, setProject] = useState<ReportSelectOption>(defaultProjectOption);

    const [formData, setFormData] = useState({
        idProjects: query.get('idProjects') ?? '',
        fromDate: query.get('fromDate') ?? '',
        toDate: query.get('toDate') ?? '',
    });

    const hasInitialQuery = useMemo(() => {
        return Boolean(query.get('idProjects') && query.get('fromDate') && query.get('toDate'));
    }, [query]);

    const autoLoadTriggeredRef = useRef(false);

    useEffect(() => {
        getProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (projects.length > 0) {
            const projectFromQuery = Number(query.get('idProjects'));
            const projectOption = projectFromQuery
                ? projects.find((opt) => Number(opt.value) === projectFromQuery)
                : projects[0];

            setProject(projectOption ?? defaultProjectOption);
            setFormData((prev) => ({
                ...prev,
                idProjects: projectOption && projectOption.value !== ''
                    ? String(projectOption.value)
                    : '',
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects]);

    useEffect(() => {
        if (
            hasInitialQuery &&
            !autoLoadTriggeredRef.current &&
            formData.idProjects &&
            formData.fromDate &&
            formData.toDate
        ) {
            handleViewReport();
            autoLoadTriggeredRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.idProjects, formData.fromDate, formData.toDate, hasInitialQuery]);

    const getProjects = () => {
        fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
            .then((resp) => resp.json())
            .then((resp) => {
                const projectOptions: ReportSelectOption[] = resp.data.map((item: any) => ({
                    label: item.name,
                    value: item.id,
                }));
                setProjects(projectOptions);
            })
            .catch((error) => {
                console.log(error, 'catch the hoop');
            });
    };

    const handleProjectChange = (selectedProject: ReportSelectOption | null) => {
        const nextProject = selectedProject ?? defaultProjectOption;
        setProject(nextProject);
        setFormData((prev) => ({
            ...prev,
            idProjects: nextProject.value !== '' ? String(nextProject.value) : '',
        }));
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleViewReport = async () => {
        if (!formData.idProjects || !formData.fromDate || !formData.toDate) {
            toast.error('Project, From Date, and To Date are required.', { position: 'bottom-right', autoClose: 4000 });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(
                process.env.REACT_APP_API_URL + 'reports/payment-summary',
                postRequestOptions(formData)
            );
            const data = await response.json();

            if (data.success) {
                setRows(data.data || []);
            } else {
                setRows([]);
                data.errors?.forEach((error: string) =>
                    toast.error(error, { position: 'bottom-right', autoClose: 4000 })
                );
            }
        } catch (error) {
            console.error('Error submitting data:', error);
            toast.error('Failed to load report. Please try again later.', {
                position: 'bottom-right',
                autoClose: 4000,
            });
        } finally {
            setLoading(false);
        }
    };

    const totalDrAmount = useMemo(
        () => rows.filter((row) => row.ledgerDrCr === 'dr').reduce((total, row) => total + Number(row.ledgerAmount || 0), 0),
        [rows]
    );

    const totalCrAmount = useMemo(
        () => rows.filter((row) => row.ledgerDrCr === 'cr').reduce((total, row) => total + Number(row.ledgerAmount || 0), 0),
        [rows]
    );

    const queryParams = useMemo(() => {
        const params = new URLSearchParams();
        Object.entries(formData).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
            }
        });
        return params;
    }, [formData]);

    return (
        <>
            <Card className='mb-4'>
                <Card.Body>
                    <Card.Title></Card.Title>
                    <Card.Text>
                        <div className='row'>
                            <div className='col-md-2'>
                                <Form.Group className="mb-3" controlId="project">
                                    <Form.Label>Project<span className='required'></span></Form.Label>
                                    <Select
                                        value={project}
                                        onChange={handleProjectChange}
                                        options={projects}
                                        menuPosition="fixed"
                                        styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                        isClearable={true}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="fromDate">
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="From Date"
                                        name='fromDate'
                                        value={formData.fromDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-3'>
                                <Form.Group className="mb-3" controlId="toDate">
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        placeholder="To Date"
                                        name='toDate'
                                        value={formData.toDate}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </div>
                            <div className='col-md-1'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <button className='btn btn-sm btn-primary w-100 p-3' onClick={handleViewReport} disabled={loading}>
                                        {loading && (
                                            <span className="spinner-border spinner-border-sm"></span>
                                        )}
                                        <span>View Report</span>
                                    </button>
                                </div>
                            </div>
                            <div className='col-md-2'>
                                <div className="form-control-sm ps-0 mt-7">
                                    <a
                                        className="btn btn-sm btn-primary p-3 mb-2 me-2"
                                        href={`${process.env.REACT_APP_BACKEND}payment_summary_report_download?` + queryParams.toString()}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        <i className='fas fa-download'></i> Download
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Card.Text>
                </Card.Body>
            </Card>

            <div className='row'>
                <div className='col-12 trial-balance-container table-responsive'>
                    <Table bordered hover>
                        <thead className="sticky-top bg-white" style={{ zIndex: 0 }}>
                            <tr>
                                <th>SL.</th>
                                <th>Payment Date</th>
                                <th>Payment ID/Voucher No.</th>
                                <th>Invoice No.</th>
                                <th>Supplier / Ledger Name</th>
                                <th>Payment Category</th>
                                <th>Payment Type</th>
                                <th className='text-end'>Amount</th>
                                <th>Narration</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center">No Data Found</td>
                                </tr>
                            )}
                            {rows.map((item, index) => (
                                <tr key={`${item.idAccVouchers}-${index}`}>
                                    <td>{index + 1}</td>
                                    <td>{item.voucherDate}</td>
                                    <td>
                                        <Link
                                            target='_blank'
                                            className="btn btn-light-primary btn-sm"
                                            to={`transaction-details/${item.idAccVouchers}`}
                                        >
                                            {item.voucherNo}
                                        </Link>
                                    </td>
                                    <td>N/A</td>
                                    <td>{item.ledgerName}</td>
                                    <td>{item.ledgerPath || '-'}</td>
                                    <td>{capitalize(item.voucherType)}</td>
                                    <td className="text-end">{numberFormat(item.ledgerAmount || 0)}</td>
                                    <td>{item.narration || '-'}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={7} className="text-end"><strong>Total</strong></td>
                                <td className="text-end">
                                    <strong>{numberFormat(totalDrAmount + totalCrAmount)}</strong>
                                </td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </div>
        </>
    );
};

export default PaymentSummaryReport;